import { motion, useReducedMotion } from 'framer-motion'
import PropTypes from 'prop-types'
import { useEffect, useId, useRef, useState } from 'react'

import { EASE } from './tokens'

// Score-Kreise wandern vom alten zum neuen Stand. Kleine Kreise lösen sich vom
// linken Kreis, ziehen sich wie Kaugummi in die Länge, reißen ab und
// verschmelzen rechts. Der Effekt kommt von einem Goo-Filter (Unschärfe plus
// harte Alpha-Kante), der nahe Kreise zu einer Masse verbindet. Mit dem
// Fortschritt schrumpft der linke Kreis, der rechte wächst.
// Bei reduzierter Bewegung stehen nur die beiden Kreise da, ohne Wanderung.

const WIDTH = 320
const HEIGHT = 112
const Y = 52
const LEFT_X = 56
const RIGHT_X = WIDTH - 56
const TRAVEL_SECONDS = 1.7
const SPAWN_MS = 420

const lerp = (from, to, share) => from + (to - from) * share

const GumTransfer = ({ fromLabel, toLabel, progress, active }) => {
  const reducedMotion = useReducedMotion()
  const filterId = `goo-${useId().replace(/:/g, '')}`
  const [bubbles, setBubbles] = useState([])
  const nextId = useRef(0)

  useEffect(() => {
    if (!active || reducedMotion) return undefined
    const spawn = () => {
      nextId.current += 1
      setBubbles((current) => [...current, nextId.current])
    }
    spawn()
    const timer = setInterval(spawn, SPAWN_MS)
    return () => clearInterval(timer)
  }, [active, reducedMotion])

  const remove = (id) =>
    setBubbles((current) => current.filter((bubble) => bubble !== id))

  const share = Math.min(1, Math.max(0, progress))
  const leftRadius = lerp(24, 15, share)
  const rightRadius = lerp(15, 24, share)

  return (
    <div className='relative mx-auto w-full max-w-xs' aria-hidden='true'>
      <svg viewBox={`0 0 ${WIDTH} ${HEIGHT}`} className='w-full'>
        <defs>
          <filter id={filterId}>
            <feGaussianBlur in='SourceGraphic' stdDeviation='5' result='blur' />
            <feColorMatrix
              in='blur'
              mode='matrix'
              values='1 0 0 0 0  0 1 0 0 0  0 0 1 0 0  0 0 0 18 -7'
              result='goo'
            />
            <feComposite in='SourceGraphic' in2='goo' operator='atop' />
          </filter>
        </defs>
        <g filter={reducedMotion ? undefined : `url(#${filterId})`}>
          <motion.circle
            cx={LEFT_X}
            cy={Y}
            className='fill-lightBlue'
            animate={{ r: leftRadius }}
            transition={{ duration: 0.4, ease: EASE.out }}
          />
          <motion.circle
            cx={RIGHT_X}
            cy={Y}
            className='fill-aivyBlue'
            animate={{ r: rightRadius }}
            transition={{ duration: 0.4, ease: EASE.out }}
          />
          {bubbles.map((id) => (
            <motion.circle
              key={id}
              cy={Y}
              className='fill-aivyBlue'
              initial={{ cx: LEFT_X, r: 11 }}
              // Langsam vom linken Kreis weg (Kaugummi), schnell über die
              // Mitte, langsam in den rechten hinein
              animate={{
                cx: [LEFT_X, LEFT_X + 34, RIGHT_X - 34, RIGHT_X],
                r: [11, 9, 9, 11]
              }}
              transition={{
                duration: TRAVEL_SECONDS,
                times: [0, 0.32, 0.72, 1],
                ease: [EASE.standard, [0.4, 0, 0.6, 1], EASE.out]
              }}
              onAnimationComplete={() => remove(id)}
            />
          ))}
        </g>
      </svg>
      <div className='-mt-3 flex justify-between text-xs font-semibold'>
        <span className='w-28 text-center text-mediumGrey'>{fromLabel}</span>
        <span className='w-28 text-center text-aivyBlue'>{toLabel}</span>
      </div>
    </div>
  )
}

GumTransfer.propTypes = {
  fromLabel: PropTypes.node.isRequired,
  toLabel: PropTypes.node.isRequired,
  // 0 bis 1
  progress: PropTypes.number,
  active: PropTypes.bool
}

GumTransfer.defaultProps = {
  progress: 0,
  active: true
}

export default GumTransfer
